import { useEffect, useRef } from 'react';

import { useMe, useUpdatePreferences } from '../hooks/useApiQuery';
import { useTheme, type ThemePreference } from '../lib/theme';

/**
 * Two-way sync between the local ThemeProvider and the server-side
 * `users.ui_theme` column.
 *
 * - On first load, if the user has no localStorage preference yet, we
 *   adopt whatever the server stored (so the choice survives a fresh
 *   install / new device). If localStorage already has a value, the
 *   local one wins — the user explicitly clicked it last session.
 * - When the user picks a theme in Settings, we PATCH the server. The
 *   PATCH is fire-and-forget; a network hiccup just means the next
 *   device won't see the choice until they pick it again.
 *
 * Lives outside ThemeProvider so the provider stays free of React
 * Query / network deps and can be tested in isolation.
 */
export function ThemeBackendBridge() {
  const me = useMe();
  const { preference, isDefault, setPreference } = useTheme();
  const update = useUpdatePreferences();
  const hydratedRef = useRef(false);
  const lastPushedRef = useRef<ThemePreference | null>(null);

  // Hydrate from server once the /v1/me data lands.
  useEffect(() => {
    if (hydratedRef.current) return;
    const fromServer = me.data?.ui_theme;
    if (!fromServer) return;
    hydratedRef.current = true;
    if (isDefault && fromServer !== preference) {
      setPreference(fromServer);
      lastPushedRef.current = fromServer;
    } else {
      // Local already had a value — that's the source of truth. Push
      // it up so the server reflects what the user actually sees.
      lastPushedRef.current = preference;
      if (preference !== fromServer) {
        update.mutate({ ui_theme: preference });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me.data?.ui_theme]);

  // Push subsequent user-driven changes.
  useEffect(() => {
    if (!hydratedRef.current) return;
    if (lastPushedRef.current === preference) return;
    lastPushedRef.current = preference;
    update.mutate({ ui_theme: preference });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preference]);

  return null;
}
